import {
  AutocompleteInputChangeReason,
  Grid,
  Typography,
  debounce,
} from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { SyntheticEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import CustomAutocompleteMulti from "../../../common/components/functional/customAutocomplete/CustomAutocompleteMulti";
import getLocale from "../../../common/helpers/getLocale";
import { AutocompleteType } from "../../../common/types/autoComplete";
import { fetchSkillDefinitions } from "../orderRowsDetailedSlice";
import { OrderFields } from "../orderTypes";

interface Options {
  skills: AutocompleteType[];
}

const initialSearchTerms = {
  skills: "",
};

type Props = {
  values: OrderFields;
  onOrderTemplateChange: (
    name: string,
    value: string | null | AutocompleteType | AutocompleteType[],
  ) => void;
};

export const Skills = ({ values, onOrderTemplateChange }: Props) => {
  const [fieldOptions, setFieldOptions] = useState<Options>({
    skills: values.orderTemplate.skills,
  });
  const [searchTerms, setSearchTerms] = useState(initialSearchTerms);
  const [open, setOpen] = useState("");
  const { t } = useTranslation(["order", "skills"]);
  const dispatch = useAppDispatch();

  const localized = useAppSelector((state) => state.localized);

  const locale = getLocale();

  const onInputChange = debounce(
    (key: string, term: string, reason?: AutocompleteInputChangeReason) => {
      setSearchTerms({ ...searchTerms, [key]: term });

      if (!term) {
        setFieldOptions({ ...fieldOptions, [key]: [] });
      }
    },
    400,
  );

  const handleOpen = (key: string) => setOpen(key);

  const onChange = (
    e: SyntheticEvent<Element, Event>,
    newValue: AutocompleteType[] | null,
    name: string,
  ) => {
    if (newValue) {
      onOrderTemplateChange(name, newValue);
    }
  };

  useEffect(() => {
    if (values.orderTemplate.workTitleId && open === "skills") {
      dispatch(
        fetchSkillDefinitions({
          ...(searchTerms.skills && { term: searchTerms.skills }),
          workTitleIds: values.orderTemplate.workTitleId,
          language: locale.code || "fi",
        }),
      ).then((action) => {
        if (isFulfilled(action)) {
          const skillOptions = action.payload.map((skill) => ({
            id: skill.id,
            name: localized.skill[skill.id],
          })) as AutocompleteType[];

          setFieldOptions((fieldOptions) => ({
            ...fieldOptions,
            skills: skillOptions,
          }));
        }
      });
    }
  }, [
    open,
    searchTerms.skills,
    t,
    locale.code,
    localized.skill,
    dispatch,
    values.orderTemplate.workTitleId,
  ]);

  const selectedSkillOptions = useMemo(() => {
    return values.orderTemplate.skills.map((skill) => ({
      id: skill.id,
      name: localized.skill[skill.id],
    }));
  }, [values.orderTemplate.skills, localized.skill]);

  const sortedSkillOptions = useMemo(() => {
    return fieldOptions.skills.sort((a, b) => a.name.localeCompare(b.name));
  }, [fieldOptions.skills]);

  return (
    <>
      <Grid container item xs={4} justifyContent="flex-end" alignItems="center">
        <Typography variant="secondary">{t("common:skills")}</Typography>
      </Grid>
      <Grid item xs={8}>
        <CustomAutocompleteMulti
          name="skills"
          defaultValue={selectedSkillOptions}
          options={sortedSkillOptions}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={onChange}
          onInputChange={onInputChange}
          onOpen={handleOpen}
          fullWidth
        />
      </Grid>
    </>
  );
};
